<!-- TEMPLATE -->
<template>
    <div :class='invoicesListClass'>
      <div class='row'>
        <div class='small-12 medium-12 large-12 hd-12 columns dashboard-content'>
          <dean-section-title content='true'>
            <span slot='title'>
              <span class='avenir-heavy'>Facturen</span>
            </span>
          </dean-section-title>
          <dean-table
            :data='invoicesData'
            :columns='columns'
            :hover="true"
            @column-click='columnClick'
          >
            <span slot="actions" slot-scope="actions">
              <dean-button v-for="(action, index) in actions.value.content"
                           v-if="canDownloadInvoice()"
                           :key="index"
                           @click.native="actionClick(action.href, action.label, action.fileName)">
                  <dean-icon
                    :key="action.label"
                    className="deanIcon"
                    :glyph="downloadIcon"
                    width="10"
                    height="10"
                    title="Service">
                  </dean-icon>
                  <span>{{ action.label }}</span>
              </dean-button>
          </span>
                </dean-table>
                <dean-pagination
                        :current-page='invoicesMetaData.pageNumber'
                        :total-items='invoicesMetaData.totalResults'
                        :items-per-page='invoicesMetaData.itemsPerPage'
                        :select-items-per-page='selectItemsPerPage'
                        :visible-pages='invoicesMetaData.visiblePages'
                        @page-changed='paginationChanged'
                        @items-per-page='itemsPerPageChanged'
                >
                </dean-pagination>
            </div>
        </div>
    </div>
</template>

<!-- SCRIPT -->

<script lang='ts'>
  // VUE
  import {
    Vue,
    Component,
  } from 'vue-property-decorator';
  import deanTableMixin from '@/mixins/deanTableMixin';
  import sideMenuPartnerMixin from '@/mixins/sideMenuPartnerMixin';
  import {mixins} from 'vue-class-component';
  import axios from 'axios';
  import {formatPrice} from '@/mixins/formatPrice';

  import {
    // SVG
    invoicesIcon,
    // COMPONENTS
    deanButton,
    deanTable,
    deanPagination,
    deanInputSelect,
    deanSectionDivider,
    deanSectionTitle,
    deanSectionTitleItem,
    deanSearchField,
    deanIcon,
    downloadIcon,
    summeryIcon,
  } from '@deanone/dean-components';

  @Component({
    components: {
      deanButton,
      deanPagination,
      deanTable,
      deanInputSelect,
      deanSectionDivider,
      deanSectionTitle,
      deanSectionTitleItem,
      deanSearchField,
      invoicesIcon,
      deanIcon,
      downloadIcon,
      summeryIcon,
    },
  })
  export default class List extends mixins(deanTableMixin, sideMenuPartnerMixin) {
    // DATA
    public downloadIcon: object = downloadIcon;
    public invoices: any = [];
    public columns: any = {
      invoiceNumber: {
        content: 'Factuurnummer',
        align: 'left',
        sort: true,
        dql: 'invoiceNumber',
      },
      partner: {
        content: 'Partnernaam',
        align: 'left',
        sort: true,
        dql: 'partner',
      },
      billingPeriod: {
        content: 'Factureringsperiode',
        align: 'left',
        sort: true,
        dql: 'billingPeriod',
      },
      invoiceTotalBruto: {
        content: 'Factuurbedrag bruto',
        align: 'left',
        sort: true,
        dql: 'invoiceTotalBruto',
      },
      invoiceTotalDiscount: {
        content: 'Factuurkorting',
        align: 'left',
        sort: true,
        dql: 'invoiceTotalDiscount',
      },
      invoiceTotalNetto: {
        content: 'Factuurbedrag netto',
        align: 'left',
        sort: true,
        dql: 'invoiceTotalNetto',
      },
      actions: {
        content: 'Factuur downloaden',
        type: 'slot',
        slot: 'actions',
        align: 'center',
        sort: false,
        dql: 'actions',
      },
    };

    // COMPUTED
    get invoicesData() {
      return this.invoices;
    }

    get invoicesMetaData() {
      return this.pageMeta;
    }

    get invoicesListClass() {
      const value = 'invoices-summary';
      return value;
    }

    public tableName(): string {
      return 'partner-invoices';
    }

    public actionClick(downloadUrl: string, label: string, name: string) {
      if (!this.canDownloadInvoice()) {
        return;
      }

      this.$store.dispatch('setLoadOverlay', true);

      axios
        .get(downloadUrl, {responseType: 'arraybuffer'})
        .then(
          (response) => {
            let fileType: string = '';
            let mimeType: string = '';

            if (label === 'PDF') {
              fileType = 'pdf';
              mimeType = 'application/pdf';
            } else if (label === 'CSV') {
              fileType = 'csv';
              mimeType = 'text/csv';
            } else if (label === 'CDR') {
              fileType = 'csv';
              mimeType = 'text/csv';
            } else if (label === 'XLSX') {
              fileType = 'xlsx';
              mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
            }

            this.downloadFile(response, name, fileType, mimeType);

            this.$store.dispatch('setLoadOverlay', false);
          },
          (error) => {
            this.$store.dispatch('setLoadOverlay', false);
          },
        );
    }

    public downloadFile(response: any, filename: string, fileType: string, mimeType: string) {
      // It is necessary to create a new blob object with mime-type explicitly set
      // otherwise only Chrome works like it should
      const newBlob = new Blob([response.data], {type: mimeType});

      // IE doesn't allow using a blob object directly as link href
      // instead it is necessary to use msSaveOrOpenBlob
      if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
        (window.navigator as any).msSaveOrOpenBlob(newBlob);

        return;
      }

      // For other browsers:
      // Create a link pointing to the ObjectURL containing the blob.
      const data = window.URL.createObjectURL(newBlob);
      const link = document.createElement('a');
      link.href = data;
      link.download = filename + '.' + fileType;
      link.click();
    }

    public loadList(data: any) {
      this.$store.dispatch('setLoadOverlay', true);

      const url = '/-/trade/api/partner.invoices.list';

      const requestData = {
        meta: {
          startIndex: this.startIndex,
          numberOfItems: data.itemsPerPage,
        },
        sort: data.sort,
        pid: this.$route.params.pid,
      };

      axios
        .post(url, requestData)
        .then(
          (response) => {
            this.invoices = [];

            for (const invoice of response.data.data) {
              const row = {
                invoiceNumber: invoice.invoiceNumber,
                partner: invoice.partner.name,
                billingPeriod: invoice.billingPeriod,
                invoiceTotalBruto: formatPrice(invoice.invoiceTotalBruto),
                invoiceTotalDiscount: formatPrice(invoice.invoiceTotalDiscount),
                invoiceTotalNetto: formatPrice(invoice.invoiceTotalNetto),
                actions: invoice.actions,
              };

              this.invoices.push(row);
            }

            this.updateMeta(response.data.meta);
            this.$store.dispatch('setLoadOverlay', false);
          },
          (error) => {
            this.$store.dispatch('setLoadOverlay', false);
          },
        );
    }

    public canDownloadInvoice(): boolean
    {
      if (this.$store.getters.getUser.privileges.includes('download-invoice')) {
        return true;
      }

      return false;
    }

    public created() {
      this.setDefaultSort('-billingPeriod');
      this.loadList(this.pageMeta);
      this.buildSideMenuPartner();
    }
  }
</script>

<!-- STYLE -->

<style lang='scss' scoped>
    @import '~@deanone/dean-components/src/scss/vars';

    .dean-button {
      margin-right: 5px;
      .dean-button-item {
        padding: 8px 20px;
      }
    }

    .deanIcon {
      margin-right: 4px;
    }
</style>
